// Sound levels for the waveform bars, from a live microphone or a finished
// clip. Levels run from 0 (silence) to 1 (the loudest point).

export const WAVEFORM_BARS = 40;

export const formatDuration = (seconds: number): string => {
  const whole = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  const m = Math.floor(whole / 60);
  const s = whole % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// Stretches levels so the loudest bar fills the waveform, which keeps a
// quiet recording readable.
export const normaliseLevels = (levels: number[]): number[] => {
  const peak = Math.max(...levels, 0);
  if (peak === 0) {
    return levels.map(() => 0);
  }
  return levels.map((level) => level / peak);
};

// The loudness of a buffer of samples, as the root mean square.
export const rms = (samples: Float32Array): number => {
  if (samples.length === 0) {
    return 0;
  }
  let sum = 0;
  for (let i = 0; i < samples.length; i += 1) {
    sum += samples[i] * samples[i];
  }
  return Math.sqrt(sum / samples.length);
};

// Spreads a clip over a fixed number of bars: the loudness of each slice
// of the sound. Empty when the browser can't decode it (or has no Web
// Audio, as in tests), in which case the waveform draws a flat line.
export const levelsFromUrl = async (
  url: string,
  bars = WAVEFORM_BARS,
): Promise<number[]> => {
  if (typeof AudioContext === 'undefined') {
    return [];
  }
  try {
    const response = await fetch(url);
    const encoded = await response.arrayBuffer();
    const context = new AudioContext();
    const buffer = await context.decodeAudioData(encoded);
    await context.close();

    const samples = buffer.getChannelData(0);
    const sliceSize = Math.max(1, Math.floor(samples.length / bars));
    const levels = Array.from({ length: bars }, (_, i) =>
      rms(samples.subarray(i * sliceSize, (i + 1) * sliceSize)),
    );
    return normaliseLevels(levels);
  } catch {
    return [];
  }
};
